import React, { useState, useEffect } from 'react';
import { ShoppingBag, Package, Calendar, CreditCard, Loader2, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, token } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/user/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-green-50 flex items-center justify-center">
        <Card className="max-w-md border-4 border-teal-300">
          <CardContent className="p-8 text-center">
            <ShoppingBag className="h-16 w-16 text-teal-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Sign in to view your orders</h2>
            <p className="text-gray-600 mb-6">Access your purchase history and receipts</p>
            <div className="space-y-3">
              <Link to="/login">
                <Button className="w-full bg-gradient-to-r from-blue-500 to-teal-500 text-white">
                  Login
                </Button>
              </Link>
              <Link to="/signup">
                <Button variant="outline" className="w-full border-2 border-teal-400">
                  Create Account
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-teal-500 mx-auto mb-4" />
          <p className="text-xl text-gray-600">Loading your orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-green-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <Link to="/profile" className="inline-flex items-center text-teal-600 hover:text-teal-700 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Profile
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-black mb-4">
            <span className="bg-gradient-to-r from-blue-600 via-teal-600 to-green-600 bg-clip-text text-transparent">
              My Orders
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto font-medium">
            Your purchase history and receipts
          </p>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingBag className="h-24 w-24 text-gray-300 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-700 mb-2">No orders yet</h2>
            <p className="text-gray-600 mb-6">You haven't made any purchases yet.</p>
            <div className="space-x-4">
              <Link to="/book">
                <Button className="bg-gradient-to-r from-blue-500 to-teal-500 text-white">
                  <Package className="h-4 w-4 mr-2" />
                  Get the Book
                </Button>
              </Link>
              <Link to="/coaching">
                <Button variant="outline" className="border-2 border-teal-400">
                  Explore Coaching
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <p className="text-gray-600">{orders.length} order{orders.length !== 1 ? 's' : ''}</p>
            
            {orders.map((order, index) => (
              <Card key={index} className="border-4 border-teal-200 hover:border-teal-400 transition-all">
                <CardHeader className="bg-gradient-to-r from-blue-500 to-teal-500 text-white p-4 rounded-t-lg">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <Package className="h-6 w-6" />
                      <span className="font-bold text-lg">{order.package_name}</span>
                    </div>
                    <Badge className={`${getStatusColor(order.status)} font-bold`}>
                      {order.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">Purchase Date</p>
                        <p className="font-medium">{formatDate(order.createdAt)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <CreditCard className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">Amount Paid</p>
                        <p className="font-bold text-green-600 text-xl">{formatCurrency(order.amount)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <ShoppingBag className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">Order ID</p>
                        <p className="font-mono text-sm text-gray-600 truncate max-w-[150px]">
                          {order.session_id?.slice(0, 20)}...
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Quick Links */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">Need help with an order?</p>
          <Link to="/contact">
            <Button variant="outline" className="border-2 border-teal-400">
              Contact Support
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Orders;
